import React from 'react';
import type { DeviceType } from './types';

interface DeviceFrameProps {
  selectedDevice: DeviceType;
  isRotated: boolean;
  frameWidth: number;
  frameHeight: number;
  totalFrameWidth: number;
  totalFrameHeight: number;
  isLoading: boolean;
  previewUrl: string;
  /** When provided, rendered via srcdoc (inherits parent origin, avoids blob: issues). */
  srcdocHtml?: string | null;
  siteId: string;
  pageId: string;
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  onIframeLoad: () => void;
}

const LoadingOverlay: React.FC = () => (
  <div className="absolute inset-0 bg-white flex items-center justify-center z-10">
    <div className="flex flex-col items-center space-y-3">
      <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      <span className="text-sm text-gray-500">Loading preview...</span>
    </div>
  </div>
);

const PreviewIframe: React.FC<{
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  previewUrl: string;
  srcdocHtml?: string | null;
  onIframeLoad: () => void;
}> = ({ iframeRef, previewUrl, srcdocHtml, onIframeLoad }) => {
  if (srcdocHtml) {
    return (
      <iframe
        ref={iframeRef}
        srcDoc={srcdocHtml}
        className="w-full h-full border-0"
        title="Responsive Preview"
        onLoad={onIframeLoad}
      />
    );
  }
  return (
    <iframe
      ref={iframeRef}
      src={previewUrl}
      className="w-full h-full border-0"
      title="Responsive Preview"
      onLoad={onIframeLoad}
    />
  );
};

export const DeviceFrame: React.FC<DeviceFrameProps> = ({
  selectedDevice,
  isRotated,
  frameWidth,
  frameHeight,
  totalFrameWidth,
  totalFrameHeight,
  isLoading,
  previewUrl,
  srcdocHtml,
  siteId,
  pageId,
  iframeRef,
  onIframeLoad,
}) => {
  // Phone/Tablet Frame
  if (selectedDevice !== 'desktop') {
    return (
      <div
        className={`bg-gray-950 rounded-[2.5rem] shadow-2xl ${
          selectedDevice === 'mobile' ? 'p-3' : 'p-4'
        }`}
        style={{
          width: totalFrameWidth,
          height: totalFrameHeight,
        }}
      >
        {/* Notch (for mobile in portrait) */}
        {selectedDevice === 'mobile' && !isRotated && (
          <div className="absolute top-1 left-1/2 -translate-x-1/2 w-24 h-6 bg-gray-950 rounded-b-2xl z-10 flex items-center justify-center">
            <div className="w-12 h-1.5 bg-gray-800 rounded-full"></div>
          </div>
        )}

        {/* Screen */}
        <div
          className="relative bg-white rounded-[2rem]"
          style={{
            width: frameWidth,
            height: frameHeight,
            overflow: 'hidden',
            clipPath: 'inset(0 round 2rem)',
          }}
        >
          {isLoading && <LoadingOverlay />}
          <PreviewIframe
            iframeRef={iframeRef}
            previewUrl={previewUrl}
            srcdocHtml={srcdocHtml}
            onIframeLoad={onIframeLoad}
          />
        </div>

        {/* Home Indicator (for mobile in portrait) */}
        {selectedDevice === 'mobile' && !isRotated && (
          <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-32 h-1 bg-gray-700 rounded-full"></div>
        )}
      </div>
    );
  }

  // Desktop Frame
  return (
    <div
      className="bg-gray-700 rounded-lg shadow-2xl overflow-hidden"
      style={{
        width: totalFrameWidth,
        height: totalFrameHeight,
      }}
    >
      {/* Browser Chrome */}
      <div className="h-8 bg-gray-800 flex items-center px-3 space-x-2">
        <div className="flex space-x-1.5">
          <div className="w-3 h-3 rounded-full bg-red-500"></div>
          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
        </div>
        <div className="flex-1 mx-4">
          <div className="bg-gray-700 rounded-md px-3 py-1 text-xs text-gray-400 truncate">
            {window.location.origin}/preview/{siteId}/{pageId}
          </div>
        </div>
      </div>

      {/* Screen */}
      <div
        className="relative bg-white"
        style={{
          width: frameWidth,
          height: frameHeight,
        }}
      >
        {isLoading && <LoadingOverlay />}
        <PreviewIframe
          iframeRef={iframeRef}
          previewUrl={previewUrl}
          srcdocHtml={srcdocHtml}
          onIframeLoad={onIframeLoad}
        />
      </div>
    </div>
  );
};
